import { memo, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useHasPermission } from '../../store/index.js'
import {
  mediaLibraryApi, type MediaBrowseEntry, type MediaBrowseResult, type MediaLibraryAsset,
} from '../../lib/api.js'
import { HttpError } from '../../lib/http.js'
import { formatFileSize, formatDuration } from '../../lib/utils.js'
import { PageHeader, PageTabs, type PageTab } from '../common/PageChrome.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import { Button } from '../ui/button.js'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu.js'
import {
  FolderPlus, Upload, Pencil, Trash2, RefreshCw, RefreshCcw, UploadCloud, LayoutGrid, List, ChevronRight,
  HardDrive, Folder, CheckCircle2, Lock, AlertTriangle, MoreHorizontal, Eye, FolderOpen, ArrowDownToLine,
  ShieldCheck, Keyboard, Film, Search, X, ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react'
import { useUploadQueue, type UploadTask } from './useUploadQueue.js'
import { UploadCenter } from './UploadCenter.js'
import { MediaPreviewDialog, type MediaOpFailure } from './MediaPreviewDialog.js'
import { MediaCard } from './MediaCard.js'
import { MediaExtBadge } from './MediaExtBadge.js'
import {
  formatBytesFr, formatClock, formatDateTimeFr, formatDayMonth, folderLabel,
} from './mediaLibraryFormat.js'
import {
  EMPTY_MEDIA_FILTER, filterMediaEntries, isFilterActive, nextSort, parseSort, serializeSort, sortMediaEntries,
  type MediaFilter, type MediaKindFilter, type MediaSort, type MediaSortKey, type MediaStateFilter,
} from './mediaLibrarySort.js'
import './MediaLibraryView.css'

const TABS: readonly PageTab[] = [
  { id: 'files',  label: 'Fichiers' },
  { id: 'health', label: 'Santé des médias' },
]

type Tab = 'files' | 'health'

/** Formats dont le serveur sait lire la durée (vidéos). */
/** Fichiers dont la durée se lit (vidéos d'antenne et musique de l'infoservice). */
const VIDEO_EXT_RE = /\.(mp4|mxf|mov|ts|mts|avi|mkv|mpg|mpeg|wmv|wav|mp3|flac|aac|m4a|ogg|aiff?)$/i
/** Taille d'un lot de lecture de durées (borne côté serveur : 40). */
const PROBE_BATCH = 20

/** Signal de rafraîchissement : dossier concerné + compteur monotone. */
interface UploadedSignal { path: string; file?: string; tick: number }
/** Demande d'ouverture d'un dossier avec un fichier mis en évidence. */
interface RevealRequest { path: string; file?: string; tick: number }
interface StorageInfo { path: string; disk: MediaBrowseResult['disk'] }

export const MediaLibraryView = memo(function MediaLibraryView() {
  const [tab, setTab] = useState<Tab>('files')
  const [storage, setStorage] = useState<StorageInfo | null>(null)
  // La file d'envoi vit au-dessus des onglets : les téléversements se poursuivent
  // même en passant sur « Santé », et le suivi flottant reste visible.
  const [uploaded, setUploaded] = useState<UploadedSignal>({ path: '', tick: 0 })
  const [reveal, setReveal] = useState<RevealRequest>({ path: '', tick: 0 })
  const onUploaded = useCallback((path: string, file?: string) => {
    setUploaded(prev => ({ path, file, tick: prev.tick + 1 }))
  }, [])
  const queue = useUploadQueue(onUploaded)

  const onReveal = useCallback((task: UploadTask) => {
    setTab('files')
    setReveal(prev => ({ path: task.destPath, file: task.resultPath, tick: prev.tick + 1 }))
  }, [])

  // Garde anti-navigation : tant que la Médiathèque est ouverte, un fichier
  // lâché n'importe où sur la page (à côté de la zone de dépôt, sur l'en-tête,
  // les onglets ou le suivi flottant) ne doit jamais faire ouvrir le fichier
  // par le navigateur — ce qui rechargeait la page et annulait l'envoi en cours.
  useEffect(() => {
    const swallowStrayFileDrag = (e: DragEvent) => {
      if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) e.preventDefault()
    }
    window.addEventListener('dragover', swallowStrayFileDrag)
    window.addEventListener('drop', swallowStrayFileDrag)
    return () => {
      window.removeEventListener('dragover', swallowStrayFileDrag)
      window.removeEventListener('drop', swallowStrayFileDrag)
    }
  }, [])

  return (
    <div className="tp-page-shell media-library-view">
      <PageHeader title="Médiathèque" />
      <div className="media-library-tabsbar">
        <PageTabs
          items={TABS}
          activeId={tab}
          onChange={id => setTab(id as Tab)}
          ariaLabel="Sections de la médiathèque"
          controlsId="media-library-panel"
          idPrefix="media-library"
        />
        {storage && (
          <span className="media-library-share" title={storage.path}>
            <HardDrive size={13} aria-hidden />
            <span className="media-library-share__path">{storage.path}</span>
            {storage.disk && (
              <>
                <span className="media-library-share__sep" aria-hidden />
                <span className="media-library-share__free">{formatBytesFr(storage.disk.freeBytes)} libres</span>
              </>
            )}
          </span>
        )}
      </div>
      <div className="tp-page-content media-library-content" id="media-library-panel">
        {tab === 'files' && (
          <FilesBrowser
            enqueue={queue.enqueue}
            enqueueReplace={queue.enqueueReplace}
            uploaded={uploaded}
            reveal={reveal}
            onStorage={setStorage}
          />
        )}
        {tab === 'health' && <HealthPanel />}
      </div>

      <UploadCenter
        tasks={queue.tasks}
        onCancel={queue.cancel}
        onCancelAll={queue.cancelAll}
        onRetry={queue.retry}
        onDismiss={queue.dismiss}
        onClearFinished={queue.clearFinished}
        onReveal={onReveal}
      />
    </div>
  )
})

// ─── Onglet Fichiers ──────────────────────────────────────────────────────────

interface FilesBrowserProps {
  enqueue:        (files: File[], destPath: string, destLabel: string) => void
  enqueueReplace: (file: File, targetSubPath: string, destPath: string, destLabel: string) => void
  uploaded:       UploadedSignal
  reveal:         RevealRequest
  onStorage:      (info: StorageInfo) => void
}

interface PreviewState { path: string; originY: number | null }
interface DragState { count: number; x: number; y: number }

const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes('Files')
const draggedFileCount = (e: React.DragEvent) =>
  Array.from(e.dataTransfer.items ?? []).filter(item => item.kind === 'file').length

const FilesBrowser = memo(function FilesBrowser({ enqueue, enqueueReplace, uploaded, reveal, onStorage }: FilesBrowserProps) {
  // Les trois permissions sont evaluees inconditionnellement : un `||` entre
  // appels de hook court-circuite et change l'ordre des hooks d'un rendu a
  // l'autre, ce que React interdit.
  const mayManagePrograms  = useHasPermission('PROGRAM_MANAGE')
  const mayManageJingles   = useHasPermission('JINGLE_MANAGE')
  const mayManageTechnical = useHasPermission('TECHNICAL_SETTINGS_MANAGE')
  const canUpload = mayManagePrograms || mayManageJingles || mayManageTechnical
  const canModify = mayManageTechnical

  const [files,       setFiles]       = useState<MediaBrowseEntry[]>([])
  const [basePath,    setBasePath]    = useState('')
  const [currentPath, setCurrentPath] = useState('')
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [durations,   setDurations]   = useState<Record<string, number | null>>({})
  const [probing,     setProbing]     = useState<ReadonlySet<string>>(new Set())
  const [preview,     setPreview]     = useState<PreviewState | null>(null)
  const [flash,       setFlash]       = useState<string | null>(null)
  const [busy,        setBusy]        = useState(false)
  const [dialog,      setDialog]      = useState<null | { kind: 'folder' | 'rename'; target?: MediaBrowseEntry }>(null)
  const [confirmDel,  setConfirmDel]  = useState<MediaBrowseEntry | null>(null)
  const [opError,     setOpError]     = useState<MediaOpFailure | null>(null)
  const [drag,        setDrag]        = useState<DragState | null>(null)
  const [viewMode,    setViewMode]    = useState<'grid' | 'list'>(() =>
    (localStorage.getItem('mediaLibraryView') === 'grid' ? 'grid' : 'list'))
  const [sort,        setSortState]   = useState<MediaSort>(() => parseSort(localStorage.getItem('mediaLibrarySort')))
  const [filter,      setFilter]      = useState<MediaFilter>(EMPTY_MEDIA_FILTER)
  const dragDepth    = useRef(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const replaceInput = useRef<HTMLInputElement>(null)
  const replaceFor   = useRef<MediaBrowseEntry | null>(null)
  const loadSeq      = useRef(0)

  const setView = useCallback((mode: 'grid' | 'list') => {
    setViewMode(mode); localStorage.setItem('mediaLibraryView', mode)
  }, [])
  // Le tri est mémorisé sur le poste ; les filtres valent pour la session.
  const setSort = useCallback((next: MediaSort) => {
    setSortState(next); localStorage.setItem('mediaLibrarySort', serializeSort(next))
  }, [])
  const toggleSort = useCallback((key: MediaSortKey) => {
    setSortState(prev => {
      const next = nextSort(prev, key)
      localStorage.setItem('mediaLibrarySort', serializeSort(next))
      return next
    })
  }, [])
  const clearFilter = useCallback(() => setFilter(EMPTY_MEDIA_FILTER), [])

  /** Lit les durées manquantes par lots ; chaque lot remplit ses lignes. */
  const probeMissing = useCallback(async (entries: MediaBrowseEntry[], seq: number) => {
    const missing = entries
      .filter(f => f.type === 'file' && f.durationSec == null && VIDEO_EXT_RE.test(f.name))
      .map(f => f.relativePath)
    if (missing.length === 0) return
    setProbing(new Set(missing))
    for (let i = 0; i < missing.length; i += PROBE_BATCH) {
      const batch = missing.slice(i, i + PROBE_BATCH)
      try {
        const { results } = await mediaLibraryApi.probeDurations(batch)
        if (seq !== loadSeq.current) return
        setDurations(prev => {
          const next = { ...prev }
          for (const r of results) next[r.subPath] = r.durationSec
          return next
        })
      } catch {
        if (seq !== loadSeq.current) return
      }
      setProbing(prev => {
        const next = new Set(prev)
        for (const p of batch) next.delete(p)
        return next
      })
    }
  }, [])

  const load = useCallback((path: string, highlight?: string) => {
    const seq = ++loadSeq.current
    setLoading(true); setError('')
    mediaLibraryApi.browse(path || undefined)
      .then(r => {
        if (seq !== loadSeq.current) return
        setFiles(r.files); setBasePath(r.path); setCurrentPath(r.subPath)
        setDurations({}); setProbing(new Set())
        onStorage({ path: r.path, disk: r.disk })
        if (highlight) setFlash(highlight)
        void probeMissing(r.files, seq)
      })
      .catch(e => { if (seq === loadSeq.current) setError(e.message ?? 'Erreur de chargement') })
      .finally(() => { if (seq === loadSeq.current) setLoading(false) })
  }, [onStorage, probeMissing])

  useEffect(() => { load('') }, [load])

  // Un envoi terminé dans le dossier affiché rafraîchit la liste et le désigne.
  const currentPathRef = useRef(currentPath)
  currentPathRef.current = currentPath
  useEffect(() => {
    if (uploaded.tick > 0 && uploaded.path === currentPathRef.current) load(currentPathRef.current, uploaded.file)
  }, [uploaded, load])

  // « Voir » dans le suivi : ouvre le dossier d'arrivée et met le fichier en évidence.
  // Les filtres sont levés : le fichier désigné ne doit pas être masqué.
  useEffect(() => {
    if (reveal.tick === 0) return
    setPreview(null)
    setFilter(EMPTY_MEDIA_FILTER)
    load(reveal.path, reveal.file)
  }, [reveal, load])

  // La mise en évidence s'éteint d'elle-même après l'animation.
  useEffect(() => {
    if (!flash) return
    const el = document.querySelector<HTMLElement>(`[data-media-path="${CSS.escape(flash)}"]`)
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    const timer = window.setTimeout(() => setFlash(null), 2200)
    return () => window.clearTimeout(timer)
  }, [flash, files])

  const openDir = useCallback((f: MediaBrowseEntry) => {
    if (f.type === 'directory') { setOpError(null); load(f.relativePath) }
  }, [load])

  const allFileEntries = useMemo(() => files.filter(f => f.type === 'file'), [files])
  // La liste affichée : filtrée puis triée. L'aperçu « précédent / suivant »
  // suit cet ordre, celui que l'opérateur a sous les yeux.
  const filtering = isFilterActive(filter)
  const visible = useMemo(() => {
    const durationOf = (f: MediaBrowseEntry) => f.durationSec ?? durations[f.relativePath] ?? null
    return sortMediaEntries(filterMediaEntries(files, filter), sort, durationOf)
  }, [files, filter, sort, durations])
  const fileEntries = useMemo(() => visible.filter(f => f.type === 'file'), [visible])
  const previewIndex = preview ? fileEntries.findIndex(f => f.relativePath === preview.path) : -1
  const previewEntry = previewIndex >= 0 ? fileEntries[previewIndex] : null

  const openEntry = useCallback((f: MediaBrowseEntry, originY: number | null = null) => {
    if (f.type === 'directory') { openDir(f); return }
    setOpError(null)
    setPreview({ path: f.relativePath, originY })
  }, [openDir])

  const closePreview = useCallback(() => { setPreview(null); setOpError(null) }, [])
  const prevFile = useCallback(() => {
    if (previewIndex > 0) { setOpError(null); setPreview({ path: fileEntries[previewIndex - 1].relativePath, originY: null }) }
  }, [fileEntries, previewIndex])
  const nextFile = useCallback(() => {
    if (previewIndex >= 0 && previewIndex < fileEntries.length - 1) {
      setOpError(null); setPreview({ path: fileEntries[previewIndex + 1].relativePath, originY: null })
    }
  }, [fileEntries, previewIndex])

  const dropLabel = `${basePath}${currentPath ? ' / ' + currentPath : ''}`
  const networkFolder = useMemo(() => {
    const sep = basePath.includes('\\') ? '\\' : '/'
    return currentPath ? `${basePath}${sep}${currentPath.split('/').join(sep)}` : basePath
  }, [basePath, currentPath])

  const startUpload = useCallback((list: FileList | File[] | null) => {
    if (!list) return
    const arr = Array.from(list)
    if (arr.length === 0) return
    setOpError(null)
    enqueue(arr, currentPathRef.current, dropLabel)
  }, [enqueue, dropLabel])

  const startReplace = useCallback((entry: MediaBrowseEntry, file: File) => {
    setOpError(null)
    enqueueReplace(file, entry.relativePath, currentPathRef.current, dropLabel)
  }, [enqueueReplace, dropLabel])

  const pickReplacement = useCallback((entry: MediaBrowseEntry) => {
    replaceFor.current = entry
    if (replaceInput.current) {
      replaceInput.current.accept = entry.ext ?? ''
      replaceInput.current.click()
    }
  }, [])

  // ─── Glisser-déposer ───
  // Pendant le survol, le navigateur ne livre que le nombre d'éléments : ni
  // nom ni taille avant le lâcher.
  const onDragEnter = useCallback((e: React.DragEvent) => {
    if (!canUpload || !isFileDrag(e)) return
    e.preventDefault(); dragDepth.current += 1
    setDrag({ count: draggedFileCount(e), x: e.clientX, y: e.clientY })
  }, [canUpload])
  const onDragOver = useCallback((e: React.DragEvent) => {
    if (!canUpload || !isFileDrag(e)) return
    e.preventDefault(); e.dataTransfer.dropEffect = 'copy'
    const { clientX: x, clientY: y } = e
    setDrag(prev => (prev && prev.x === x && prev.y === y ? prev : { count: prev?.count || draggedFileCount(e), x, y }))
  }, [canUpload])
  const onDragLeave = useCallback((e: React.DragEvent) => {
    if (!canUpload) return
    e.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDrag(null)
  }, [canUpload])
  const onDrop = useCallback((e: React.DragEvent) => {
    if (!canUpload) return
    e.preventDefault(); dragDepth.current = 0; setDrag(null)
    startUpload(e.dataTransfer.files)
  }, [canUpload, startUpload])

  // ─── Opérations disque ───
  const runOp = useCallback(async (op: () => Promise<unknown>, after?: { highlight?: string; closePreview?: boolean }) => {
    setBusy(true); setOpError(null)
    try {
      await op()
      setDialog(null); setConfirmDel(null)
      if (after?.closePreview) setPreview(null)
      load(currentPathRef.current, after?.highlight)
    } catch (e) {
      setConfirmDel(null)
      if (e instanceof HttpError && e.status === 409) {
        // Le corps porte la liste des items bloquants (à l'antenne / imminents).
        const blockers = (e.body as { blockers?: MediaOpFailure['blockers'] } | undefined)?.blockers
        setOpError({ message: e.message, blockers })
      } else {
        setOpError({ message: e instanceof Error ? e.message : 'Opération refusée' })
      }
    } finally {
      setBusy(false)
    }
  }, [load])

  const askRename = useCallback((target: MediaBrowseEntry) => { setOpError(null); setDialog({ kind: 'rename', target }) }, [])
  const askDelete = useCallback((target: MediaBrowseEntry) => { setOpError(null); setConfirmDel(target) }, [])

  const segments  = currentPath ? currentPath.split('/') : []
  const nFiles    = allFileEntries.length
  const nDirs     = files.length - nFiles
  const totalSize = allFileEntries.reduce((s, f) => s + f.size, 0)
  const visibleSize = fileEntries.reduce((s, f) => s + f.size, 0)
  const stats = [
    `${nDirs} dossier${nDirs !== 1 ? 's' : ''}`,
    `${nFiles} fichier${nFiles !== 1 ? 's' : ''}`,
    ...(totalSize > 0 ? [formatBytesFr(totalSize)] : []),
  ].join(' · ')

  return (
    <div
      className="media-library-files"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="media-library-toolbar">
        <div className="media-library-toolbar__crumbs">
          <nav className="media-library-crumbs" aria-label="Fil d’Ariane">
            <button
              type="button"
              className="media-library-crumbs__btn"
              onClick={() => load('')}
              aria-current={currentPath ? undefined : 'page'}
            >Médiathèque</button>
            {segments.map((seg, i) => {
              const target = segments.slice(0, i + 1).join('/')
              const last = i === segments.length - 1
              return (
                <span key={target} className="media-library-crumbs__seg">
                  <ChevronRight size={14} aria-hidden />
                  <button
                    type="button"
                    className="media-library-crumbs__btn"
                    onClick={() => load(target)}
                    aria-current={last ? 'page' : undefined}
                  >{seg}</button>
                </span>
              )
            })}
          </nav>
          <span className="media-library-toolbar__meta">{loading && files.length === 0 ? 'Chargement…' : stats}</span>
        </div>

        <div className="media-library-toolbar__actions">
          <div className="media-library-viewtoggle" role="group" aria-label="Affichage">
            <button type="button" className={viewMode === 'grid' ? 'is-active' : undefined} onClick={() => setView('grid')} aria-pressed={viewMode === 'grid'} aria-label="Vue grille" title="Grille"><LayoutGrid size={15} aria-hidden /></button>
            <button type="button" className={viewMode === 'list' ? 'is-active' : undefined} onClick={() => setView('list')} aria-pressed={viewMode === 'list'} aria-label="Vue liste" title="Liste"><List size={15} aria-hidden /></button>
          </div>
          <Button variant="outline" onClick={() => load(currentPath)} disabled={loading}>
            <RefreshCw size={14} aria-hidden className={loading ? 'media-library-spin' : undefined} /> Rafraîchir
          </Button>
          {canUpload && (
            <>
              <Button variant="outline" onClick={() => setDialog({ kind: 'folder' })}><FolderPlus size={14} aria-hidden /> Nouveau dossier</Button>
              <Button onClick={() => fileInputRef.current?.click()}><Upload size={14} aria-hidden /> Importer</Button>
              <input ref={fileInputRef} type="file" multiple hidden onChange={e => { startUpload(e.target.files); e.target.value = '' }} />
            </>
          )}
          <input
            ref={replaceInput}
            type="file"
            hidden
            onChange={e => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (file && replaceFor.current) startReplace(replaceFor.current, file)
            }}
          />
        </div>
      </div>

      {opError && !previewEntry && (
        <div className="tp-feedback tp-feedback--error media-library-files__op-error" role="alert">
          <span><AlertTriangle size={14} aria-hidden /> {opError.message}</span>
          {opError.blockers && opError.blockers.length > 0 && (
            <ul className="media-library-files__blockers">
              {opError.blockers.map(b => (
                <li key={b.itemId}>{b.channelName} — {b.title} ({b.status}) · {formatDateTimeFr(b.startTime)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {files.length > 0 && (
        <MediaFilterBar
          filter={filter}
          onFilter={setFilter}
          onClear={clearFilter}
          sort={sort}
          onSort={setSort}
          showSort={viewMode === 'grid'}
          shown={visible.length}
          total={files.length}
        />
      )}

      <div className={`media-library-frame${drag ? ' is-dragover' : ''}`}>
        {error && <div className="tp-state tp-feedback--error" role="alert">⚠ {error}</div>}

        {!error && loading && files.length === 0 && (
          <div className="tp-state media-library-frame__state" aria-live="polite"><Spinner /> Chargement…</div>
        )}

        {!error && !loading && files.length === 0 && (
          canUpload ? (
            <button type="button" className="media-library-files__dropzone" onClick={() => fileInputRef.current?.click()}>
              <UploadCloud size={26} aria-hidden />
              <span className="media-library-files__dropzone-lead">Dossier vide — glissez des fichiers pour les importer</span>
              <span className="media-library-files__dropzone-sub">ou cliquez pour parcourir · destination : {currentPath || 'racine'}</span>
            </button>
          ) : <div className="tp-state is-empty">Dossier vide</div>
        )}

        {!error && files.length > 0 && visible.length === 0 && (
          <div className="tp-state is-empty media-library-frame__state media-library-frame__nomatch" role="status">
            <span>Aucun élément ne correspond aux filtres dans ce dossier.</span>
            <Button variant="outline" size="sm" onClick={clearFilter}><X size={14} aria-hidden /> Effacer les filtres</Button>
          </div>
        )}

        {!error && visible.length > 0 && viewMode === 'list' && (
          <div className={`media-table${loading ? ' is-loading' : ''}`}>
            <div className="media-table__head" role="group" aria-label="Trier la liste">
              <span />
              <SortHeader label="Nom"     sortKey="name"     sort={sort} onSort={toggleSort} />
              <SortHeader label="Durée"   sortKey="duration" sort={sort} onSort={toggleSort} numeric />
              <SortHeader label="Taille"  sortKey="size"     sort={sort} onSort={toggleSort} numeric />
              <SortHeader label="Modifié" sortKey="modified" sort={sort} onSort={toggleSort} />
              <SortHeader label="État"    sortKey="state"    sort={sort} onSort={toggleSort} />
            </div>
            <ul className="media-table__rows" aria-label="Contenu du dossier">
              {visible.map(f => (
                <MediaRow
                  key={f.relativePath}
                  entry={f}
                  duration={f.durationSec ?? durations[f.relativePath] ?? null}
                  probing={probing.has(f.relativePath)}
                  flash={flash === f.relativePath}
                  active={preview?.path === f.relativePath}
                  canModify={canModify}
                  onOpen={openEntry}
                  onRename={askRename}
                  onDelete={askDelete}
                  onReplace={pickReplacement}
                />
              ))}
            </ul>
            <footer className="media-table__foot">
              <span className="media-table__legend"><CheckCircle2 size={12} aria-hidden className="media-state--checked" /> contrôlé</span>
              <span className="media-table__legend"><Lock size={12} aria-hidden className="media-state--scheduled" /> diffusion programmée</span>
              <span className="media-table__legend"><AlertTriangle size={12} aria-hidden className="media-state--failed" /> contrôle en échec</span>
              <span className="media-table__total">
                {filtering ? `${visible.length} sur ${files.length}` : files.length} élément{files.length > 1 ? 's' : ''}
                {(filtering ? visibleSize : totalSize) > 0 ? ` · ${formatBytesFr(filtering ? visibleSize : totalSize)}` : ''}
              </span>
            </footer>
          </div>
        )}

        {!error && visible.length > 0 && viewMode === 'grid' && (
          <div className="media-library-files__grid">
            {visible.map(f => (
              <div key={f.relativePath} data-media-path={f.relativePath} className={flash === f.relativePath ? 'media-library-flash' : undefined}>
                <MediaCard
                  file={f}
                  selected={preview?.path === f.relativePath}
                  onClick={() => openEntry(f)}
                  onDoubleClick={() => openDir(f)}
                />
              </div>
            ))}
          </div>
        )}

        {drag && (
          <div className="media-drop" aria-hidden>
            <span className="media-drop__icon"><ArrowDownToLine size={26} /></span>
            <span className="media-drop__title">Déposez pour importer dans « {folderLabel(currentPath)} »</span>
            <span className="media-drop__sub">
              {drag.count > 0 ? `${drag.count} fichier${drag.count > 1 ? 's' : ''} · ` : ''}{networkFolder}
            </span>
            <span className="media-drop__hints">
              <span className="media-drop__hint"><Film size={12} /> MP4 · MOV · MXF acceptés</span>
              <span className="media-drop__hint"><ShieldCheck size={12} /> Contrôle média à l’arrivée</span>
              <span className="media-drop__hint"><Keyboard size={12} /> Échap pour annuler</span>
            </span>
          </div>
        )}
      </div>

      {drag && drag.count > 0 && (
        <span key={drag.count} className="media-drop__count" style={{ left: drag.x, top: drag.y }} aria-hidden>{drag.count}</span>
      )}

      {previewEntry && (
        <MediaPreviewDialog
          entry={previewEntry}
          position={previewIndex + 1}
          total={fileEntries.length}
          folderLabel={networkFolder}
          originY={preview?.originY}
          canModify={canModify}
          busy={busy}
          failure={opError}
          onPrev={prevFile}
          onNext={nextFile}
          onClose={closePreview}
          onRename={askRename}
          onDelete={askDelete}
          onReplace={startReplace}
        />
      )}

      {dialog && (
        <NameDialog
          title={dialog.kind === 'folder' ? 'Nouveau dossier' : `Renommer « ${dialog.target?.name} »`}
          initial={dialog.kind === 'rename' ? (dialog.target?.name ?? '') : ''}
          confirmLabel={dialog.kind === 'folder' ? 'Créer' : 'Renommer'}
          busy={busy}
          onClose={() => setDialog(null)}
          onSubmit={name => {
            const parent = currentPathRef.current
            const created = parent ? `${parent}/${name}` : name
            if (dialog.kind === 'folder') {
              void runOp(() => mediaLibraryApi.createFolder(name, parent || undefined), { highlight: created })
            } else if (dialog.target) {
              const target = dialog.target
              void runOp(() => mediaLibraryApi.rename(target.relativePath, name), { highlight: created, closePreview: true })
            }
          }}
        />
      )}

      {confirmDel && (
        <ModalShell title="Supprimer" onClose={() => setConfirmDel(null)}>
          <p className="media-library-files__confirm">
            Supprimer définitivement <strong>{confirmDel.name}</strong>{confirmDel.type === 'directory' ? ' et tout son contenu' : ''} du disque ?
            Un fichier à l’antenne ou imminent sera refusé.
          </p>
          <div className="media-library-files__confirm-actions">
            <Button variant="outline" onClick={() => setConfirmDel(null)} disabled={busy}>Annuler</Button>
            <Button variant="destructive" onClick={() => runOp(() => mediaLibraryApi.remove(confirmDel.relativePath), { closePreview: true })} disabled={busy}>
              {busy && <Spinner />} Supprimer
            </Button>
          </div>
        </ModalShell>
      )}
    </div>
  )
})

// ─── Filtres et tri ─────────────────────────────────────────────────────────────

const SORT_LABELS: Record<MediaSortKey, string> = {
  name: 'nom', duration: 'durée', size: 'taille', modified: 'date de modification', state: 'état',
}

/** Sens d'un tri en mots, pour les lecteurs d'écran et l'infobulle. */
function sortDirLabel(key: MediaSortKey, dir: MediaSort['dir']): string {
  switch (key) {
    case 'name':     return dir === 'asc' ? 'de A à Z' : 'de Z à A'
    case 'modified': return dir === 'desc' ? 'du plus récent au plus ancien' : 'du plus ancien au plus récent'
    case 'duration': return dir === 'desc' ? 'du plus long au plus court' : 'du plus court au plus long'
    case 'size':     return dir === 'desc' ? 'du plus lourd au plus léger' : 'du plus léger au plus lourd'
    case 'state':    return dir === 'desc' ? 'échecs et diffusions programmées d’abord' : 'fichiers contrôlés d’abord'
  }
}

/** En-tête de colonne cliquable : un clic trie, un second inverse le sens. */
const SortHeader = memo(function SortHeader({ label, sortKey, sort, onSort, numeric = false }: {
  label: string; sortKey: MediaSortKey; sort: MediaSort; onSort: (key: MediaSortKey) => void; numeric?: boolean
}) {
  const active = sort.key === sortKey
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown
  const state = active ? `trié ${sortDirLabel(sortKey, sort.dir)}` : 'non trié'
  return (
    <button
      type="button"
      className={`media-table__sort${numeric ? ' media-table__num' : ''}${active ? ' is-active' : ''}`}
      onClick={() => onSort(sortKey)}
      aria-pressed={active}
      aria-label={`Trier par ${SORT_LABELS[sortKey]} (${state})`}
      title={active ? `Trié ${sortDirLabel(sortKey, sort.dir)} — cliquer pour inverser` : `Trier par ${SORT_LABELS[sortKey]}`}
    >
      <span className="media-table__sort-label">{label}</span>
      <Icon size={11} aria-hidden className="media-table__sort-icon" />
    </button>
  )
})

const KIND_OPTIONS: readonly { value: MediaKindFilter; label: string }[] = [
  { value: 'all', label: 'Tous les types' },
  { value: 'video', label: 'Vidéos' },
  { value: 'image', label: 'Images' },
  { value: 'audio', label: 'Musique' },
]
const STATE_OPTIONS: readonly { value: MediaStateFilter; label: string }[] = [
  { value: 'all', label: 'Tous les états' },
  { value: 'SCHEDULED', label: 'Diffusion programmée' },
  { value: 'FAILED', label: 'Contrôle en échec' },
  { value: 'CHECKED', label: 'Contrôlé' },
  { value: 'UNCHECKED', label: 'Non contrôlé' },
]
/** Tri de la vue grille, qui n'a pas d'en-têtes de colonne. */
const SORT_PRESETS: readonly { value: string; label: string }[] = [
  { value: 'modified:desc', label: 'Plus récents' },
  { value: 'modified:asc', label: 'Plus anciens' },
  { value: 'name:asc', label: 'Nom A → Z' },
  { value: 'name:desc', label: 'Nom Z → A' },
  { value: 'duration:desc', label: 'Plus longs' },
  { value: 'size:desc', label: 'Plus lourds' },
  { value: 'state:desc', label: 'À surveiller d’abord' },
]

const MediaFilterBar = memo(function MediaFilterBar({
  filter, onFilter, onClear, sort, onSort, showSort, shown, total,
}: {
  filter: MediaFilter; onFilter: (next: MediaFilter) => void; onClear: () => void
  sort: MediaSort; onSort: (next: MediaSort) => void; showSort: boolean
  shown: number; total: number
}) {
  const active = isFilterActive(filter)
  const sortValue = serializeSort(sort)
  return (
    <div className="media-library-filters" role="search" aria-label="Filtrer le dossier">
      <label className="media-library-filters__search">
        <Search size={14} aria-hidden />
        <input
          type="search"
          className="tp-input"
          value={filter.query}
          onChange={e => onFilter({ ...filter, query: e.target.value })}
          onKeyDown={e => { if (e.key === 'Escape' && filter.query) { e.stopPropagation(); onFilter({ ...filter, query: '' }) } }}
          placeholder="Filtrer par nom…"
          aria-label="Filtrer par nom"
        />
      </label>
      <select
        className="tp-select media-library-filters__select"
        value={filter.kind}
        onChange={e => onFilter({ ...filter, kind: e.target.value as MediaKindFilter })}
        aria-label="Type de fichier"
        data-active={filter.kind !== 'all' || undefined}
      >
        {KIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <select
        className="tp-select media-library-filters__select"
        value={filter.state}
        onChange={e => onFilter({ ...filter, state: e.target.value as MediaStateFilter })}
        aria-label="État du média"
        data-active={filter.state !== 'all' || undefined}
      >
        {STATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {showSort && (
        <label className="media-library-filters__sort">
          <ArrowUpDown size={13} aria-hidden />
          <select
            value={sortValue}
            onChange={e => onSort(parseSort(e.target.value))}
            aria-label="Trier les fichiers"
          >
            {!SORT_PRESETS.some(p => p.value === sortValue) && (
              <option value={sortValue}>{`${SORT_LABELS[sort.key]}, ${sortDirLabel(sort.key, sort.dir)}`}</option>
            )}
            {SORT_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
      )}
      <span className="media-library-filters__count" role="status">
        {active ? `${shown} sur ${total}` : ''}
      </span>
      {active && (
        <Button variant="ghost" size="sm" onClick={onClear}><X size={14} aria-hidden /> Effacer</Button>
      )}
    </div>
  )
})

// ─── Ligne de la liste ──────────────────────────────────────────────────────────

interface MediaRowProps {
  entry:     MediaBrowseEntry
  duration:  number | null
  probing:   boolean
  flash:     boolean
  active:    boolean
  canModify: boolean
  onOpen:    (entry: MediaBrowseEntry, originY: number | null) => void
  onRename:  (entry: MediaBrowseEntry) => void
  onDelete:  (entry: MediaBrowseEntry) => void
  onReplace: (entry: MediaBrowseEntry) => void
}

const MediaRow = memo(function MediaRow({
  entry, duration, probing, flash, active, canModify, onOpen, onRename, onDelete, onReplace,
}: MediaRowProps) {
  const isDir = entry.type === 'directory'
  const open = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    onOpen(entry, rect.top + rect.height / 2)
  }, [entry, onOpen])

  const className = [
    'media-table__row',
    isDir ? 'is-dir' : '',
    flash ? 'media-library-flash' : '',
    active ? 'is-active' : '',
  ].filter(Boolean).join(' ')

  return (
    <li className={className} data-media-path={entry.relativePath}>
      <button
        type="button"
        className="media-table__open"
        onClick={open}
        aria-label={isDir ? `Ouvrir le dossier ${entry.name}` : `Aperçu de ${entry.name}`}
      >
        <span className="media-table__type">
          {isDir ? <Folder size={17} aria-hidden className="media-table__folder" /> : <MediaExtBadge name={entry.name} />}
        </span>
        <span className={`media-table__name${isDir ? ' is-dir' : ''}`} title={entry.name}>{entry.name}</span>
        <span className={`media-table__num media-table__duration${duration == null ? ' is-empty' : ' is-known'}`}>
          {isDir ? '—' : probing && duration == null ? <span className="media-table__probing" /> : formatClock(duration)}
        </span>
        <span className="media-table__num media-table__size">
          {isDir
            ? (entry.itemCount != null ? `${entry.itemCount} élément${entry.itemCount > 1 ? 's' : ''}` : '—')
            : formatBytesFr(entry.size)}
        </span>
        <span className="media-table__date">{formatDateTimeFr(entry.modifiedAt)}</span>
        <span className="media-table__state"><EntryState entry={entry} /></span>
      </button>

      <span className="media-table__menu">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="media-table__menu-btn" aria-label={`Actions sur ${entry.name}`}>
              <MoreHorizontal size={16} aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="media-table__menu-content">
            <DropdownMenuItem onSelect={() => onOpen(entry, null)}>
              {isDir ? <FolderOpen size={14} aria-hidden /> : <Eye size={14} aria-hidden />} {isDir ? 'Ouvrir' : 'Aperçu'}
            </DropdownMenuItem>
            {canModify && (
              <>
                {!isDir && (
                  <DropdownMenuItem onSelect={() => onReplace(entry)}>
                    <RefreshCcw size={14} aria-hidden /> Remplacer le média
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => onRename(entry)}>
                  <Pencil size={14} aria-hidden /> Renommer
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => onDelete(entry)}>
                  <Trash2 size={14} aria-hidden /> Supprimer
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </li>
  )
})

/** Pictogramme d'état d'un fichier, avec son explication au survol. */
const EntryState = memo(function EntryState({ entry }: { entry: MediaBrowseEntry }) {
  if (entry.type === 'directory' || !entry.state) return null
  if (entry.state === 'FAILED') {
    return (
      <span className="media-state media-state--failed" title={`Contrôle en échec (${entry.assetStatus ?? 'inconnu'})`}>
        <AlertTriangle size={15} aria-hidden /><span className="sr-only">Contrôle en échec</span>
      </span>
    )
  }
  if (entry.state === 'SCHEDULED') {
    const count = entry.upcomingCount ?? 0
    return (
      <span
        className="media-state media-state--scheduled"
        title={`${count} diffusion${count > 1 ? 's' : ''} programmée${count > 1 ? 's' : ''} — prochaine le ${formatDateTimeFr(entry.nextBroadcastAt)}`}
      >
        <Lock size={13} aria-hidden />
        <span className="media-state__date">{formatDayMonth(entry.nextBroadcastAt)}</span>
        <span className="sr-only">Diffusion programmée</span>
      </span>
    )
  }
  return (
    <span className="media-state media-state--checked" title="Média contrôlé">
      <CheckCircle2 size={15} aria-hidden /><span className="sr-only">Contrôlé</span>
    </span>
  )
})

// ─── Dialogue de saisie de nom ─────────────────────────────────────────────────

const NameDialog = memo(function NameDialog({
  title, initial, confirmLabel, busy, onClose, onSubmit,
}: {
  title: string; initial: string; confirmLabel: string; busy: boolean
  onClose: () => void; onSubmit: (name: string) => void
}) {
  const [value, setValue] = useState(initial)
  return (
    <ModalShell title={title} onClose={onClose}>
      <form
        onSubmit={e => { e.preventDefault(); if (value.trim()) onSubmit(value.trim()) }}
        className="media-library-files__name-form"
      >
        <input
          className="tp-input"
          value={value}
          onChange={e => setValue(e.target.value)}
          autoFocus
          placeholder="Nom"
          aria-label="Nom"
        />
        <div className="media-library-files__confirm-actions">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button type="submit" disabled={busy || !value.trim()}>{busy && <Spinner />} {confirmLabel}</Button>
        </div>
      </form>
    </ModalShell>
  )
})

// ─── Onglet Santé ───────────────────────────────────────────────────────────────

const STATUS_TONE: Record<string, string> = {
  OK: 'success', UNCHECKED: 'muted', MISSING: 'danger',
  INVALID_CODEC: 'danger', DURATION_MISMATCH: 'warning',
}

const HealthPanel = memo(function HealthPanel() {
  const [assets,  setAssets]  = useState<MediaLibraryAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState('')

  const load = useCallback(() => {
    setLoading(true); setError('')
    mediaLibraryApi.listAssets()
      .then(r => setAssets(r.assets))
      .catch(e => setError(e.message ?? 'Erreur de chargement'))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  return (
    <div className="media-library-health">
      <div className="media-library-health__head">
        <span className="media-library-health__count">{assets.length} média{assets.length !== 1 ? 's' : ''} référencé{assets.length !== 1 ? 's' : ''}</span>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw size={14} aria-hidden /> Rafraîchir</Button>
      </div>
      {loading && <div className="tp-state" aria-live="polite"><Spinner /> Chargement…</div>}
      {error && <div className="tp-state tp-feedback--error" role="alert">⚠ {error}</div>}
      {!loading && !error && (
        <div className="media-library-health__list">
          {assets.map(a => (
            <div key={a.id} className="media-library-health__row">
              <span className={`catalog-status-badge catalog-status-badge--${STATUS_TONE[a.status] ?? 'muted'}`}>{a.status}</span>
              <span className="media-library-health__name">{a.fileName}</span>
              <span className="media-library-health__meta">
                {a.program && <span>{a.program.title}</span>}
                {a.durationSec != null && <span>· {formatDuration(a.durationSec)}</span>}
                {a.resolution && <span>· {a.resolution}</span>}
                {a.fileSizeBytes && <span>· {formatFileSize(Number(a.fileSizeBytes))}</span>}
              </span>
              {a.lastError && <span className="media-library-health__err" title={a.lastError}>⚠</span>}
            </div>
          ))}
          {assets.length === 0 && <div className="tp-state is-empty">Aucun média référencé</div>}
        </div>
      )}
    </div>
  )
})
